import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Shield, Lock, Crown, User, AlertTriangle } from 'lucide-react';
import { ADMIN_PATH } from '../../config/adminPath';

const ProtectedRoute = ({ children, requiredRole, requiredSpace, allowSuperAdmin = true }) => {
  const { user, loading, canAccessSpace, hasRole, isSuperAdmin } = useAuth();
  const location = useLocation();

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#34B0D8]"></div>
          <p className="mt-4 text-gray-600">Vérification des autorisations...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return (
      <Navigate 
        to={requiredSpace === 'admin' || !requiredSpace ? `/${ADMIN_PATH}` : '/login'}
        state={{ 
          from: location,
          spaceType: requiredSpace 
        }} 
        replace 
      />
    );
  }

  // Check super admin access
  if (allowSuperAdmin && isSuperAdmin()) {
    return children;
  }

  // Check specific role
  if (requiredRole && !hasRole(requiredRole)) {
    return <AccessDenied userRole={user.role} requiredRole={requiredRole} />;
  }

  // Check space access
  if (requiredSpace && !canAccessSpace(requiredSpace)) {
    return <AccessDenied userRole={user.role} requiredSpace={requiredSpace} />;
  }

  return children;
};

const AccessDenied = ({ userRole, requiredRole, requiredSpace }) => {
  const getIcon = () => {
    if (requiredRole === 'super_admin' || requiredSpace === 'admin') {
      return <Crown size={48} className="text-red-500" />;
    }
    return <Shield size={48} className="text-red-500" />;
  };

  const getMessage = () => {
    if (requiredSpace) {
      const spaceNames = {
        admin: 'administrateur',
        client: 'client',
        partner: 'courtier'
      };
      return `Vous n'avez pas accès à l'espace ${spaceNames[requiredSpace] || requiredSpace}.`;
    }
    
    if (requiredRole) {
      const roleNames = {
        admin: 'administrateur',
        client: 'client',
        partner: 'courtier',
        super_admin: 'super administrateur'
      };
      return `Cette page nécessite le rôle ${roleNames[requiredRole] || requiredRole}.`;
    }
    
    return 'Vous n\'avez pas les permissions nécessaires pour accéder à cette page.';
  };

  const getCurrentRoleDisplay = () => {
    const roleNames = {
      admin: 'Administrateur',
      client: 'Client',
      partner: 'Courtier',
      super_admin: 'Super Administrateur'
    };
    return roleNames[userRole] || userRole;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="bg-white rounded-xl shadow-lg p-8">
          
          {/* Icon */}
          <div className="flex justify-center mb-6">
            {getIcon()}
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Accès refusé
          </h1>

          {/* Message */}
          <p className="text-gray-600 mb-4">
            {getMessage()}
          </p>

          {/* Current Role */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center">
              <User size={20} className="text-gray-500 mr-2" />
              <span className="text-sm text-gray-700">
                Rôle actuel: <strong>{getCurrentRoleDisplay()}</strong>
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={() => window.history.back()}
              className="w-full bg-[#34B0D8] text-white py-3 px-4 rounded-lg hover:bg-[#2A9BC1] transition-colors font-semibold"
            >
              Retour
            </button>
            
            <a
              href="/"
              className="block w-full bg-gray-200 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
            >
              Accueil
            </a>
          </div>

          {/* Contact Info */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-center text-sm text-gray-500">
              <AlertTriangle size={16} className="mr-2" />
              <span>
                Besoin d'aide ? Contactez{' '}
                <a href="mailto:gestion@asarcsg.com" className="text-[#34B0D8] hover:underline">
                  gestion@asarcsg.com
                </a>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProtectedRoute;